import { useAppTranslation } from '../i18n/useAppTranslation';
import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Fab, Paper, Typography, TextField, IconButton,
  Stack, Avatar, CircularProgress, Collapse, Tooltip
} from '@mui/material';
import {
  SmartToy, Close, Send, Person, AutoAwesome
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const ChatBot: React.FC = () => {
  const { tr, language } = useAppTranslation();
  const location = useLocation();
  // Toujours en bas à droite, discret
  const isMessaging = location.pathname === '/messages';
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'Bonjour ! Je suis G1SportBot 🤖\nComment puis-je vous aider à utiliser G1Sport ?' }
  ]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: Message = { role: 'user', content: text };
    const historyToSend = [...messages];
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // Ajouter un message assistant vide pour le streaming
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    const token = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL || 'https://g1sport.fr/api';

    try {
      const res = await fetch(`${baseUrl}/chatbot/chat/stream/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': language,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ message: text, history: historyToSend }),
      });

      if (!res.ok || !res.body) {
        throw new Error('Erreur serveur');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.error) throw new Error(data.error);
            if (data.token) {
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: 'assistant',
                  content: updated[updated.length - 1].content + data.token,
                };
                return updated;
              });
            }
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (e: any) {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'assistant',
          content: `⚠️ Erreur de connexion au service IA.`,
        };
        return updated;
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Bouton flottant discret */}
      <Tooltip title="Assistant G1Sport" placement="left">
        <Fab
          size="small"
          onClick={() => setOpen(o => !o)}
          sx={{
            position: 'fixed',
            bottom: isMessaging ? 72 : 24,
            right: 16,
            zIndex: 1300,
            width: 40,
            height: 40,
            minHeight: 'unset',
            background: 'linear-gradient(135deg, #00bcd4, #0097a7)',
            color: '#fff',
            opacity: open ? 1 : 0.25,
            boxShadow: '0 2px 10px rgba(0,188,212,0.35)',
            transition: 'opacity 0.2s, box-shadow 0.2s',
            '&:hover': { opacity: 1, boxShadow: '0 4px 18px rgba(0,188,212,0.55)', background: 'linear-gradient(135deg, #00acc1, #00838f)' }
          }}
        >
          {open ? <Close sx={{ fontSize: 18 }} /> : <SmartToy sx={{ fontSize: 18 }} />}
        </Fab>
      </Tooltip>

      {/* Fenêtre chat */}
      <Collapse in={open} timeout={250}>
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            bottom: isMessaging ? 120 : 72,
            right: 16,
            zIndex: 1300,
            width: { xs: 'calc(100vw - 32px)', sm: 360 },
            height: 520,
            borderRadius: 4,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid rgba(0,188,212,0.3)',
            bgcolor: 'background.paper'
          }}
        >
          {/* Header */}
          <Box sx={{
            p: 2, display: 'flex', alignItems: 'center', gap: 1.5,
            background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
            borderBottom: '1px solid rgba(0,188,212,0.2)'
          }}>
            <Avatar sx={{ bgcolor: '#00bcd4', width: 36, height: 36 }}>
              <AutoAwesome sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="white">G1SportBot</Typography>
              <Typography variant="caption" color="#00bcd4">{tr("Assistant IA · G1Sport")}</Typography>
            </Box>
            <IconButton aria-label={tr("Fermer")} onClick={() => setOpen(false)} sx={{ ml: 'auto', color: 'grey.400' }} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {messages.map((msg, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="flex-end"
                justifyContent={msg.role === 'user' ? 'flex-end' : 'flex-start'}>
                {msg.role === 'assistant' && (
                  <Avatar sx={{ bgcolor: '#00bcd4', width: 28, height: 28, mb: 0.5 }}>
                    <SmartToy sx={{ fontSize: 16 }} />
                  </Avatar>
                )}
                <Box
                  sx={{
                    maxWidth: '78%',
                    px: 1.5, py: 1,
                    borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    bgcolor: msg.role === 'user'
                      ? 'linear-gradient(135deg, #00bcd4, #0097a7)'
                      : 'action.hover',
                    background: msg.role === 'user'
                      ? 'linear-gradient(135deg, #00bcd4, #0097a7)'
                      : undefined,
                    color: msg.role === 'user' ? '#fff' : 'text.primary',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {msg.role === 'assistant' ? tr(msg.content) : msg.content}
                  </Typography>
                </Box>
                {msg.role === 'user' && (
                  <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28, mb: 0.5 }}>
                    <Person sx={{ fontSize: 16 }} />
                  </Avatar>
                )}
              </Stack>
            ))}
            {loading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar sx={{ bgcolor: '#00bcd4', width: 28, height: 28 }}>
                  <SmartToy sx={{ fontSize: 16 }} />
                </Avatar>
                <Box sx={{ px: 2, py: 1, borderRadius: 3, bgcolor: 'action.hover' }}>
                  <CircularProgress size={14} sx={{ color: '#00bcd4' }} />
                </Box>
              </Stack>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box sx={{
            p: 1.5, borderTop: '1px solid', borderColor: 'divider',
            display: 'flex', gap: 1, alignItems: 'flex-end'
          }}>
            <TextField
              fullWidth multiline maxRows={3} size="small"
              placeholder={tr("Posez votre question...")}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              disabled={loading}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '0.875rem' } }}
            />
            <IconButton aria-label={tr("Envoyer le message")}
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: '#00bcd4', color: '#fff', borderRadius: 2,
                '&:hover': { bgcolor: '#0097a7' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
              }}
            >
              <Send fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Collapse>
    </>
  );
};

export default ChatBot;
